/**
 * Hook to fetch operations metrics data
 */

import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSalonStore } from "@/lib/store/salon";
import { GET_COMPREHENSIVE_REPORT } from "../graphql/report-queries";
import { formatReportDate } from "../lib/report-date-range";
import { useReportsV2Store } from "../store/use-reports-v2-store";
import { createComparableValue } from "../types";
import type { OperationsData } from "../types/operations-types";

export function useOperationsData() {
  const { currentSalon } = useSalonStore();
  const { currentPeriod, comparisonPeriod } = useReportsV2Store();

  // Fetch current period data
  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: currentPeriod
          ? formatReportDate(currentPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: currentPeriod
          ? formatReportDate(currentPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !currentPeriod,
  });

  // Fetch comparison period data
  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !comparisonPeriod,
  });

  const loading = currentLoading || comparisonLoading;
  const error = currentError || comparisonError;
  const isReady = !(!currentSalon || !currentSalon.timezone || !currentPeriod || !comparisonPeriod);

  // Transform GraphQL data into operations format
  const operationsData: OperationsData | null = useMemo(() => {
    if (!isReady) return null;
    if (!currentData?.comprehensiveReport || !comparisonData?.comprehensiveReport) return null;

    const current = currentData.comprehensiveReport;
    const comparison = comparisonData.comprehensiveReport;

    // Return null if operations data is not available (non-premium)
    if (!current.operations || !comparison.operations) return null;

    return {
      operations: {
        cancellationRate: {
          type: "absolute",
          label: "Cancellation Rate",
          value: createComparableValue(
            current.operations.appointmentCancellationRate,
            comparison.operations.appointmentCancellationRate,
            true, // inverse: lower is better
            true // isPercentageValue
          ),
          format: "percentage",
          inverse: true,
        },
        noShowRate: {
          type: "absolute",
          label: "No-Show Rate",
          value: createComparableValue(
            current.operations.noShowRate,
            comparison.operations.noShowRate,
            true, // inverse: lower is better
            true // isPercentageValue
          ),
          format: "percentage",
          inverse: true,
        },
        averageServiceDuration: {
          type: "absolute",
          label: "Avg Service Duration",
          value: createComparableValue(
            current.operations.averageServiceDuration,
            comparison.operations.averageServiceDuration
          ),
          format: "duration",
        },
        serviceDurationDistribution: {
          current: current.operations.serviceDurationDistribution || [],
          previous: comparison.operations.serviceDurationDistribution || [],
        },
        averageBookingLeadTime: {
          type: "absolute",
          label: "Avg Booking Lead Time (days)",
          value: createComparableValue(
            current.operations.averageBookingLeadTime,
            comparison.operations.averageBookingLeadTime
          ),
          format: "number",
        },
        bookingLeadTimeDistribution: {
          current: current.operations.bookingLeadTimeDistribution || [],
          previous: comparison.operations.bookingLeadTimeDistribution || [],
        },
        peakHours: current.operations.peakHours || [],
      },
    };
  }, [currentData, comparisonData, isReady]);

  if (!isReady) {
    return { data: null, loading: false, error: null };
  }

  return {
    data: operationsData,
    loading,
    error,
  };
}
